import { Component, Property } from '@wonderlandengine/api';

/**
 * RemotePlayerManager — WLE component that renders a mesh for each connected
 * multiplayer peer at their broadcasted world position.
 *
 * Setup (in WLE editor):
 *  1. Place this component on any scene object (e.g. the Player or a Manager object).
 *  2. Set `templateObject` to a hidden scene object that is the full remote-player
 *     prefab. Add two named child objects inside it:
 *       - one named `laserChildName`        (default "RemoteLaser")       — the laser beam mesh
 *       - one named `bulletOriginChildName` (default "RemoteBulletOrigin") — muzzle anchor
 *     Both children are found by name after cloning, so scale/offset survive intact.
 *  3. The template is cloned into a pool; each peer gets one clone with all children.
 *
 * Runtime API (called by multiplayer.js):
 *  window.RemotePlayers.spawnPeer(peerId)
 *  window.RemotePlayers.despawnPeer(peerId)
 *  window.RemotePlayers.setPeerPosition(peerId, worldX, worldY, worldZ)
 *  window.RemotePlayers.getPeerBulletOrigin(peerId) → WLEObject | null
 */
export class RemotePlayerManager extends Component {
    static TypeName = 'remote-player-manager';

    static Properties = {
        /** Scene object that is the full remote-player prefab (with laser + bullet origin children). */
        templateObject: Property.object(),
        /** Pre-warmed pool size (grows on demand if more peers join). */
        poolSize: Property.int(8),
        /** Name of the laser-beam child object inside the prefab. */
        laserChildName: Property.string('RemoteLaser'),
        /** Name of the bullet-origin child object inside the prefab. */
        bulletOriginChildName: Property.string('RemoteBulletOrigin'),
        /** Vertical offset applied to remote avatar positions (eye → avatar centre). */
        avatarYOffset: Property.float(0.5),
    };

    // ── Lifecycle ──────────────────────────────────────────────────────────────

    start() {
        if (!this.templateObject) {
            console.error('[RemotePlayerManager] templateObject not set in editor!');
            return;
        }

        // Hide the template so it never renders itself
        this.templateObject.active = false;

        // Base rotation is still needed for yaw composition in update()
        this._baseRotation = new Float32Array(4);
        this.templateObject.getRotationWorld(this._baseRotation);

        /**
         * Object pool.
         * Each entry: { obj, laserObj, bulletOrigin, peerId, placed, yaw, posBuf, worldPosBuf }
         * peerId === null  →  available for reuse.
         */
        this._pool = [];

        /** Active peers: peerId → pool entry */
        this._active = new Map();

        /** Latest world-space positions broadcast from network. */
        this._peerPositions = new Map(); // peerId → [wx, wy, wz, yaw, fwdX, fwdY, fwdZ]

        /** Cached reference to vt.object so we can re-parent pool entries. */
        this._vtObject = null;

        // Pre-warm — parenting to vt.object happens lazily in spawnPeer()
        // because VoxelTreadmill may not have called start() yet.
        for (let i = 0; i < this.poolSize; i++) {
            this._pool.push(this._createPoolEntry());
        }

        window.RemotePlayers = this;
        console.log('[RemotePlayerManager] ready, pool size =', this.poolSize);
    }

    onDestroy() {
        for (const entry of this._pool) {
            try { entry.obj.destroy(); } catch (_) {}
        }
        this._pool = [];
        this._active.clear();
        this._peerPositions.clear();
        if (window.RemotePlayers === this) window.RemotePlayers = null;
    }

    // ── Public API (called by multiplayer.js via window.RemotePlayers) ─────────

    /**
     * Activate an avatar for a newly connected peer.
     * Idempotent — safe to call multiple times for the same peerId.
     * @param {string} peerId
     */
    spawnPeer(peerId) {
        if (this._active.has(peerId)) return;

        let entry = this._pool.find(e => e.peerId === null);
        if (!entry) {
            entry = this._createPoolEntry();
            this._pool.push(entry);
        }

        entry.peerId = peerId;
        entry.obj.active = true;
        this._active.set(peerId, entry);

        // Parent to the VoxelTreadmill terrain object so world-scrolling is
        // handled automatically — no manual offset calculation needed.
        this._ensureParentedToVT(entry);

        console.log('[RemotePlayerManager] spawned avatar for', peerId.slice(-6));
    }

    /**
     * Return a peer's avatar to the pool.
     * @param {string} peerId
     */
    despawnPeer(peerId) {
        const entry = this._active.get(peerId);
        if (!entry) return;

        entry.obj.active = false;
        if (entry.laserObj) entry.laserObj.active = false;
        entry.peerId = null;
        entry.placed = false;
        this._active.delete(peerId);
        this._peerPositions.delete(peerId);
        console.log('[RemotePlayerManager] despawned avatar for', peerId.slice(-6));
    }

    /**
     * Store the latest world-space position for a peer.
     * Called every 100 ms from the multiplayer pos message handler.
     */
    setPeerPosition(peerId, wx, wy, wz, yaw = 0, fwdX = 0, fwdY = 0, fwdZ = -1) {
        this._peerPositions.set(peerId, [wx, wy, wz, yaw, fwdX, fwdY, fwdZ]);
    }

    /**
     * Show or hide the mining laser beam on a peer's avatar.
     * Called by multiplayer.js when a { type:'laser' } message arrives.
     */
    setPeerLaser(peerId, on) {
        const entry = this._active.get(peerId);
        if (!entry?.laserObj) return;
        entry.laserObj.active = on;
    }

    /**
     * Return the bullet-origin scene object for a peer, or null if not found.
     * Use this in fps-pistol / multiplayer to position remote muzzle flashes.
     * @param {string} peerId
     * @returns {import('@wonderlandengine/api').Object3D | null}
     */
    getPeerBulletOrigin(peerId) {
        return this._active.get(peerId)?.bulletOrigin ?? null;
    }

    // ── Per-frame: translate world coords → scene coords ──────────────────────

    update(dt) {
        if (this._active.size === 0) return;

        const vt = window.VoxelTreadmill;
        if (!vt) return;

        const LERP = Math.min(1.0, dt * 12);

        for (const [peerId, entry] of this._active) {
            const pos = this._peerPositions.get(peerId);
            if (!pos) continue;

            const tx = pos[0];
            const ty = pos[1] - this.avatarYOffset; // eye → avatar centre
            const tz = pos[2];

            const cur = entry.posBuf;
            entry.obj.getPositionLocal(cur);

            if (!entry.placed) {
                entry.obj.setPositionLocal([tx, ty, tz]);
                entry.placed = true;
            } else {
                entry.obj.setPositionLocal([
                    cur[0] + (tx - cur[0]) * LERP,
                    cur[1] + (ty - cur[1]) * LERP,
                    cur[2] + (tz - cur[2]) * LERP,
                ]);
            }

            // ── Yaw rotation ─────────────────────────────────────────────────
            const targetYaw = pos[3] ?? 0;
            let prevYaw = entry.yaw ?? targetYaw;
            let delta = targetYaw - prevYaw;
            if (delta >  Math.PI) delta -= 2 * Math.PI;
            if (delta < -Math.PI) delta += 2 * Math.PI;
            const lerpedYaw = prevYaw + delta * LERP;
            entry.yaw = lerpedYaw;

            const hy = lerpedYaw / 2;
            const sy = Math.sin(hy), cy = Math.cos(hy);
            const [bx, by, bz, bw] = this._baseRotation;
            entry.obj.setRotationWorld([
                cy * bx + sy * bz,
                cy * by + sy * bw,
                cy * bz - sy * bx,
                cy * bw - sy * by,
            ]);

            // ── Laser beam ────────────────────────────────────────────────────
            if (entry.laserObj) {
                const ap = entry.worldPosBuf;
                // Use bulletOrigin world pos as the laser eye if available,
                // otherwise fall back to avatar centre + eye offset.
                if (entry.bulletOrigin) {
                    entry.bulletOrigin.getPositionWorld(ap);
                } else {
                    entry.obj.getPositionWorld(ap);
                    ap[1] += this.avatarYOffset;
                }

                const fwdX = pos[4] ?? 0;
                const fwdY = pos[5] ?? 0;
                const fwdZ = pos[6] ?? -1;

                // Get the laser object's local Z scale to determine its half-length
                const laserScale = new Float32Array(3);
                entry.laserObj.getScalingLocal(laserScale);
                const halfLen = laserScale[2] * 0.5;

                entry.laserObj.setPositionWorld([
                    ap[0] + fwdX * halfLen,
                    ap[1] + fwdY * halfLen,
                    ap[2] + fwdZ * halfLen,
                ]);

                const upVec = Math.abs(fwdY) > 0.9
                    ? [0, 0, fwdY > 0 ? -1 : 1]
                    : [0, 1, 0];
                entry.laserObj.lookAt([
                    ap[0] + fwdX * 1000,
                    ap[1] + fwdY * 1000,
                    ap[2] + fwdZ * 1000,
                ], upVec);
            }
        }
    }

    // ── Private helpers ────────────────────────────────────────────────────────

    _ensureParentedToVT(entry) {
        const vt = window.VoxelTreadmill;
        if (!vt?.object) return;

        if (this._vtObject !== vt.object) {
            this._vtObject = vt.object;
        }

        if (entry.obj.parent !== this._vtObject) {
            entry.obj.parent = this._vtObject;
        }
    }

    /**
     * Recursively find the first child (or descendant) of `root` whose name
     * matches `name`. Returns null if not found.
     * @param {import('@wonderlandengine/api').Object3D} root
     * @param {string} name
     */
    _findChildByName(root, name) {
        for (const child of root.children) {
            if (child.name === name) return child;
            const found = this._findChildByName(child, name);
            if (found) return found;
        }
        return null;
    }

    /** Clone the prefab and locate the named laser + bullet-origin children. */
    _createPoolEntry() {
        const obj = this.templateObject.clone(null);
        obj.active = false;

        const laserObj = this.laserChildName
            ? this._findChildByName(obj, this.laserChildName)
            : null;
        if (laserObj) {
            laserObj.active = false;
        } else if (this.laserChildName) {
            console.warn(`[RemotePlayerManager] child "${this.laserChildName}" not found in prefab — laser disabled for this clone.`);
        }

        const bulletOrigin = this.bulletOriginChildName
            ? this._findChildByName(obj, this.bulletOriginChildName)
            : null;
        if (!bulletOrigin && this.bulletOriginChildName) {
            console.warn(`[RemotePlayerManager] child "${this.bulletOriginChildName}" not found in prefab — bullet origin unavailable.`);
        }

        return {
            obj,
            laserObj,
            bulletOrigin,
            peerId: null,
            placed: false,
            yaw: 0,
            posBuf: new Float32Array(3),
            worldPosBuf: new Float32Array(3),
        };
    }
}
